import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Select from '../../components/common/Select';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import FileManager from '../../components/common/FileManager';
import ChatBox from '../../components/chat/ChatBox';
import Toast from '../../components/common/Toast';
import { Request } from '../../types/request';

interface User {
  id: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
}

interface Area {
  id: string;
  name: string;
}

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canDelete } = usePermission();
  const [request, setRequest] = useState<Request | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  // タブをURLクエリパラメータで同期
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') || (window.innerWidth < 1024 ? 'chat' : 'detail')) as 'detail' | 'preview' | 'chat';
  const setActiveTab = (tab: typeof activeTab) => {
    setSearchParams(prev => {
      prev.set('tab', tab);
      return prev;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get<Request>(`/api/requests/${id}`);
        if (cancelled) return;
        setRequest(data);
      } catch {
        if (cancelled) return;
        navigate('/requests');
      } finally {
        if (!cancelled) setLoading(false);
      }

      try {
        const usersData = await api.get<{ items: User[] }>('/api/users?status=active');
        if (cancelled) return;
        setUsers(Array.isArray(usersData?.items) ? usersData.items : []);
      } catch {
        if (!cancelled) setUsers([]);
      }

      try {
        const companiesData = await api.get<{ items: Company[] }>('/api/companies?status=active&limit=1000');
        if (cancelled) return;
        setCompanies(Array.isArray(companiesData?.items) ? companiesData.items : []);
      } catch {
        if (!cancelled) setCompanies([]);
      }

      try {
        const areasData = await api.get<{ items: Area[] }>('/api/masters/areas?limit=1000');
        if (cancelled) return;
        setAreas(Array.isArray(areasData?.items) ? areasData.items : []);
      } catch {
        if (!cancelled) setAreas([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const fetchRequest = async () => {
    setLoading(true);
    try {
      const data = await api.get<Request>(`/api/requests/${id}`);
      setRequest(data);
    } catch {
      navigate('/requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      setUsers(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setUsers([]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await api.get<{ items: Company[] }>('/api/companies?status=active&limit=1000');
      setCompanies(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setCompanies([]);
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await api.get<{ items: Area[] }>('/api/masters/areas?limit=1000');
      setAreas(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setAreas([]);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete('/api/requests/' + id);
      navigate('/requests');
    } catch {
      setToast({ message: '要請の削除に失敗しました', type: 'error' });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (loading) return;
    try {
      await api.put(`/api/requests/${id}`, { status: newStatus });
      fetchRequest();
    } catch {
      setToast({message: 'ステータスの変更に失敗しました', type: 'error'});
    }
  };

  const handleAssigneeChange = async (assigneeId: string) => {
    if (loading) return;
    try {
      await api.put(`/api/requests/${id}`, { assignedTo: assigneeId });
      fetchRequest();
    } catch {
      setToast({message: '担当者の変更に失敗しました', type: 'error'});
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'info' | 'success' | 'danger'> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'danger',
    };
    const labels: Record<string, string> = {
      pending: '対応待ち',
      in_progress: '対応中',
      completed: '完了',
      cancelled: 'キャンセル',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, 'default' | 'warning' | 'danger'> = {
      low: 'default',
      normal: 'default',
      high: 'warning',
      urgent: 'danger',
    };
    const labels: Record<string, string> = {
      low: '低',
      normal: '通常',
      high: '高',
      urgent: '緊急',
    };
    return <Badge variant={variants[priority] || 'default'}>{labels[priority] || priority}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">要請が見つかりません</p>
        <Link to="/requests" className="text-blue-600 hover:underline mt-4 inline-block">
          一覧に戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2 lg:space-y-6">
      {/* ヘッダー */}
      <div className="flex items-start gap-2">
        <Button variant="ghost" onClick={() => navigate('/requests')} className="flex-shrink-0 p-1 mt-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap text-xs">
            <span className="text-gray-500">{request.requestNumber}</span>
            {getStatusBadge(request.status)}
            {getPriorityBadge(request.priority)}
          </div>
          <h1 className="text-sm lg:text-lg font-bold text-gray-900 dark:text-white line-clamp-2">{request.title}</h1>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button onClick={() => setShowEditModal(true)} className="text-xs px-2 py-1">編集</Button>
          {canDelete('requests') && (
            <Button onClick={() => setShowDeleteConfirm(true)} className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700">削除</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側：タブで切り替え */}
        <div className="lg:col-span-2 space-y-6">
          {/* タブナビゲーション */}
          <div className="flex gap-1 border-b dark:border-gray-700">
            {/* スマホのみチャットタブを表示 */}
            <button
              type="button"
              onClick={() => setActiveTab('chat')}
              className={`lg:hidden px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'chat'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              チャット
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('detail')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'detail'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              詳細
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'preview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              プレビュー
              {(request.pdfUrl || (request.attachments && request.attachments.length > 0)) && (
                <span className="ml-2 bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                  {(request.attachments?.length || 0) + (request.pdfUrl ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* 詳細タブ */}
          {activeTab === 'detail' && (
            <>
          {/* 基本情報 */}
          <Card title="基本情報">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">企業名</span>
                <p className="font-medium text-gray-900 dark:text-white">{request.companyName || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">エリア</span>
                <p className="font-medium text-gray-900 dark:text-white">{request.areaName || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">担当者</span>
                <div className="flex items-center gap-2">
                  <Select
                    value={request.assignedTo || ''}
                    onChange={(e) => handleAssigneeChange(e.target.value)}
                    className="flex-1"
                  >
                    <option value="">未割当</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </Select>
                </div>
              </div>
              <div>
                <span className="text-gray-500">ステータス</span>
                <Select
                  value={request.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="pending">対応待ち</option>
                  <option value="in_progress">対応中</option>
                  <option value="completed">完了</option>
                  <option value="cancelled">キャンセル</option>
                </Select>
              </div>
              <div>
                <span className="text-gray-500">期限日</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {request.dueDate ? new Date(request.dueDate).toLocaleDateString('ja-JP') : '-'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">作成日</span>
                <p className="font-medium text-gray-900 dark:text-white">{formatDate(request.createdAt)}</p>
              </div>
            </div>
          </Card>

          {/* 内容 */}
          <Card title="内容">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{request.content}</p>
          </Card>

          {/* メール情報 */}
          {request.emailSubject && (
            <Card title="メール情報">
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">送信者:</span>
                  <span className="font-medium">{request.emailFrom}</span>
                  <span className="text-gray-500">受信日時:</span>
                  <span>{request.emailDate ? formatDate(request.emailDate) : '-'}</span>
                </div>
                <div className="border-t dark:border-gray-700 pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">{request.emailSubject}</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {request.emailBody}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* PDF表示 */}
          {request.pdfUrl && (
            <Card title="添付PDF">
              <div className="border dark:border-gray-700 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                <object
                  data={request.pdfUrl}
                  type="application/pdf"
                  className="w-full h-[600px]"
                >
                  <div className="flex flex-col items-center justify-center h-[600px] p-8 text-center">
                    <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">PDFをブラウザ内で表示できません</p>
                    <a
                      href={request.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      PDFを新しいタブで開く
                    </a>
                  </div>
                </object>
              </div>
              <div className="mt-4">
                <a
                  href={request.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  新しいタブで開く →
                </a>
              </div>
            </Card>
          )}

          {/* 添付ファイル - FileManager使用 */}
          <Card title="添付ファイル">
            <FileManager
              entityType="request"
              entityId={request.id}
              title=""
              showUpload={true}
              showDelete={true}
              showDownload={true}
              showPreview={true}
              layout="list"
              accept="*/*"
              maxFiles={20}
            />
          </Card>
            </>
          )}

          {/* プレビュータブ */}
          {activeTab === 'preview' && (
            <div className="space-y-6">
              {/* PDF/画像がない場合 */}
              {!request.pdfUrl && (!request.attachments || request.attachments.length === 0) && (
                <Card>
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-500">プレビューできる資料がありません</p>
                    <p className="text-sm text-gray-400 mt-1">PDF・画像ファイルをアップロードすると、ここにプレビューが表示されます</p>
                  </div>
                </Card>
              )}

              {/* PDFプレビュー */}
              {request.pdfUrl && (
                <Card title="PDF資料">
                  <div className="border dark:border-gray-700 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <object
                      data={request.pdfUrl}
                      type="application/pdf"
                      className="w-full h-[700px]"
                    >
                      {/* フォールバック: objectタグで表示できない場合 */}
                      <div className="flex flex-col items-center justify-center h-[700px] p-8 text-center">
                        <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                          PDFをブラウザ内で表示できません
                        </p>
                        <a
                          href={request.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          PDFを新しいタブで開く
                        </a>
                      </div>
                    </object>
                  </div>
                  <div className="mt-4 flex gap-4">
                    <a
                      href={request.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      新しいタブで開く
                    </a>
                    <a
                      href={request.pdfUrl}
                      download
                      className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      ダウンロード
                    </a>
                  </div>
                </Card>
              )}

              {/* 画像プレビュー */}
              {request.attachments && request.attachments.filter(a => a.type?.startsWith('image/')).length > 0 && (
                <Card title="画像資料">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {request.attachments.filter(a => a.type?.startsWith('image/')).map((attachment, index) => (
                      <div key={attachment.id || index} className="border dark:border-gray-700 rounded-lg overflow-hidden">
                        <img
                          src={attachment.url}
                          alt={attachment.name}
                          className="w-full h-auto max-h-[400px] object-contain bg-gray-100 dark:bg-gray-800"
                        />
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border-t dark:border-gray-600">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{attachment.name}</p>
                          <div className="flex gap-4 mt-2">
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              拡大表示
                            </a>
                            <a
                              href={attachment.url}
                              download={attachment.name}
                              className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              ダウンロード
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* その他のPDFファイル */}
              {request.attachments && request.attachments.filter(a => a.type === 'application/pdf').length > 0 && (
                <Card title="その他のPDF">
                  <div className="space-y-4">
                    {request.attachments.filter(a => a.type === 'application/pdf').map((attachment, index) => (
                      <div key={attachment.id || index} className="border dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{attachment.name}</span>
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs"
                            >
                              表示
                            </a>
                            <a
                              href={attachment.url}
                              download={attachment.name}
                              className="text-blue-600 hover:underline text-xs"
                            >
                              DL
                            </a>
                          </div>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-800">
                          <object
                            data={attachment.url}
                            type="application/pdf"
                            className="w-full h-[500px]"
                          >
                            <div className="flex flex-col items-center justify-center h-[500px] p-8 text-center">
                              <svg className="w-12 h-12 text-red-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm">プレビュー表示できません</p>
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                              >
                                新しいタブで開く
                              </a>
                            </div>
                          </object>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* スマホ用チャットタブ */}
          {activeTab === 'chat' && (
            <div className="lg:hidden fixed left-0 right-0 bottom-0 z-10 bg-white dark:bg-gray-800" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 160px)' }}>
              {id && user?.id && (
                <ChatBox
                  entityType="request"
                  entityId={id}
                  currentUserId={user.id}
                  title=""
                  className="h-full"
                />
              )}
            </div>
          )}
        </div>

        {/* 右側：チャット（PC専用） */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden h-[calc(100dvh-180px)] sticky top-4">
            {id && user?.id && (
              <ChatBox
                entityType="request"
                entityId={id}
                currentUserId={user.id}
                title="チャット"
                className="h-full"
              />
            )}
          </div>
        </div>
      </div>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="要請を編集" size="xl">
        <EditRequestForm
          request={request}
          users={users}
          companies={companies}
          areas={areas}
          onSave={() => {
            setShowEditModal(false);
            fetchRequest();
          }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="要請を削除" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            「{request.title}」を削除しますか？この操作は取り消せません。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>キャンセル</Button>
            <Button onClick={handleDelete} loading={deleting} className="bg-red-600 hover:bg-red-700">削除</Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

// 編集フォームコンポーネント
function EditRequestForm({
  request,
  users,
  companies,
  areas,
  onSave,
  onCancel,
}: {
  request: Request;
  users: User[];
  companies: Company[];
  areas: Area[];
  onSave: () => void;
  onCancel: () => void;
}) {
  // 日付をYYYY-MM-DD形式に変換
  const formatDateForInput = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const [formData, setFormData] = useState({
    title: request.title,
    companyId: request.companyId || '',
    areaId: request.areaId || '',
    content: request.content,
    status: request.status,
    priority: request.priority,
    assignedTo: request.assignedTo || '',
    dueDate: formatDateForInput(request.dueDate),
    emailSubject: request.emailSubject || '',
    emailBody: request.emailBody || '',
    emailFrom: request.emailFrom || '',
    emailDate: request.emailDate ? request.emailDate.slice(0, 16) : '',
  });
  const [pdfUrl, setPdfUrl] = useState(request.pdfUrl || '');
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; url: string; type: string }>>(
    request.attachments || []
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editToast, setEditToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setEditToast({message: 'PDFファイルのみアップロードできます', type: 'error'});
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setEditToast({message: 'ファイルサイズは20MB以下にしてください', type: 'error'});
      return;
    }

    setUploading(true);
    try {
      const url = await api.uploadFile(file, { entityType: 'request', entityId: request.id });
      setPdfUrl(url);
      // 新しいPDFをattachmentsにも追加
      setUploadedFiles((prev) => {
        const filtered = prev.filter((f) => f.type !== 'application/pdf' || f.url !== request.pdfUrl);
        return [...filtered, { name: file.name, url, type: file.type }];
      });
    } catch {
      setEditToast({message: 'PDFのアップロードに失敗しました', type: 'error'});
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const newFiles: Array<{ name: string; url: string; type: string }> = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 20 * 1024 * 1024) {
          setEditToast({message: `${file.name}: ファイルサイズは20MB以下にしてください`, type: 'error'});
          continue;
        }
        const url = await api.uploadFile(file, { entityType: 'request', entityId: request.id });
        newFiles.push({ name: file.name, url, type: file.type });
      }
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    } catch {
      setEditToast({message: 'ファイルのアップロードに失敗しました', type: 'error'});
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemovePdf = () => {
    setPdfUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.title.trim() || !formData.companyId || !formData.content.trim()) {
      setEditToast({message: 'タイトル、企業、内容は必須です', type: 'error'});
      return;
    }
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        areaId: formData.areaId || null,
        assignedTo: formData.assignedTo || null,
        dueDate: formData.dueDate || null,
        emailSubject: formData.emailSubject || null,
        emailBody: formData.emailBody || null,
        emailFrom: formData.emailFrom || null,
        emailDate: formData.emailDate || null,
        pdfUrl: pdfUrl || null,
        attachments: uploadedFiles.length > 0 ? uploadedFiles : null,
      };
      await api.put(`/api/requests/${request.id}`, submitData);
      onSave();
    } catch {
      setEditToast({message: '更新に失敗しました', type: 'error'});
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* 基本情報 */}
      <div className="border-b dark:border-gray-700 pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">基本情報</h3>
        <Input
          label="タイトル"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          maxLength={500}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">企業 <span className="text-red-500">*</span></label>
            <Select name="companyId" value={formData.companyId} onChange={handleChange} required>
              <option value="">企業を選択...</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">エリア</label>
            <Select name="areaId" value={formData.areaId} onChange={handleChange}>
              <option value="">エリアを選択...</option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* ステータス・優先度・担当 */}
      <div className="border-b dark:border-gray-700 pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">管理情報</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ステータス</label>
            <Select name="status" value={formData.status} onChange={handleChange}>
              <option value="pending">対応待ち</option>
              <option value="in_progress">対応中</option>
              <option value="completed">完了</option>
              <option value="cancelled">キャンセル</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先度</label>
            <Select name="priority" value={formData.priority} onChange={handleChange}>
              <option value="low">低</option>
              <option value="normal">通常</option>
              <option value="high">高</option>
              <option value="urgent">緊急</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">担当者</label>
            <Select name="assignedTo" value={formData.assignedTo} onChange={handleChange}>
              <option value="">未割当</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <Input label="期限日" name="dueDate" type="date" value={formData.dueDate} onChange={handleChange} />
          </div>
        </div>
      </div>

      {/* 内容 */}
      <div className="border-b dark:border-gray-700 pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">内容</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容 <span className="text-red-500">*</span></label>
          <textarea
            name="content"
            value={formData.content}
            onChange={handleChange}
            rows={4}
            required
            maxLength={10000}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            placeholder="要請の詳細内容を入力してください..."
          />
        </div>
      </div>

      {/* メインPDFアップロード */}
      <div className="border-b dark:border-gray-700 pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">メインPDF</h3>
        {pdfUrl ? (
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-lg">
            <svg className="w-8 h-8 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">メインPDF</p>
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">プレビュー</a>
            </div>
            <button
              type="button"
              onClick={handleRemovePdf}
              className="p-1 text-red-500 hover:text-red-700"
              title="削除"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfUpload}
              className="hidden"
              disabled={uploading}
            />
            {uploading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                <span className="text-sm text-gray-500">アップロード中...</span>
              </div>
            ) : (
              <>
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm text-gray-500">メインPDFをアップロード</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* 添付ファイル */}
      <div className="border-b dark:border-gray-700 pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">添付ファイル</h3>
        {uploadedFiles.length > 0 && (
          <div className="space-y-2 mb-3">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-lg">
                <svg className="w-6 h-6 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="flex-1 text-sm truncate">{file.name}</span>
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">表示</a>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="p-1 text-red-500 hover:text-red-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          <input
            type="file"
            accept=".pdf,image/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span className="text-sm text-gray-500">ファイルを追加</span>
        </label>
      </div>

      {/* メール情報（オプション） */}
      <div className="pb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">メール情報（オプション）</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="メール件名" name="emailSubject" value={formData.emailSubject} onChange={handleChange} maxLength={500} />
          <Input label="メール送信者" name="emailFrom" value={formData.emailFrom} onChange={handleChange} maxLength={255} />
          <div className="sm:col-span-2">
            <Input label="メール受信日時" name="emailDate" type="datetime-local" value={formData.emailDate} onChange={handleChange} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">メール本文</label>
            <textarea
              name="emailBody"
              value={formData.emailBody}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="メール本文を貼り付けてください..."
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onCancel} className="w-full sm:w-auto">
          キャンセル
        </Button>
        <Button type="submit" loading={saving} disabled={uploading} className="w-full sm:w-auto">
          保存
        </Button>
      </div>
    </form>
      {editToast && (
        <Toast message={editToast.message} type={editToast.type} onClose={() => setEditToast(null)} />
      )}
    </>
  );
}

